/**
 * API-keys tab for the tenant detail page (PR-4b).
 *
 * - Lists every key on the tenant (active + revoked) via
 *   ``GET /admin-api/v1/api-keys/?tenant_id=…``.
 * - Mint button opens a Dialog → confirm the kind → posts to
 *   ``POST /admin-api/v1/api-keys/`` → opens a non-dismissable
 *   reveal modal carrying the plaintext (server returns it ONCE).
 * - Per-row "Revoke" with a confirm Dialog.
 *
 * Visibility note: tenant-role operators with a grant on T can both
 * read AND mutate keys on T. The "Mint" / "Revoke" buttons are
 * always rendered when the API list returned successfully — the
 * server is the authority on the write check.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  asApiError,
  getApiKeys,
  mintApiKey,
  revokeApiKey,
  type ApiKeyKind,
  type ApiKeyMintResponse,
  type ApiKeyRow,
} from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { ApiKeyRevealModal } from "./ApiKeyRevealModal";

const KIND_OPTIONS: ReadonlyArray<{
  value: ApiKeyKind;
  label: string;
  description: string;
}> = [
  {
    value: "sk",
    label: "Secret key (sk_*)",
    description: "Server-side calls. Full access. Treat as production credential.",
  },
  {
    value: "pk",
    label: "Publishable key (pk_*)",
    description: "Read-only metadata. Safe for client-side surfaces.",
  },
  {
    value: "ak",
    label: "Admin key (ak_*)",
    description: "Bridge-admin only. Will be retired with #48.",
  },
] as const;


export function ApiKeysTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const [mintOpen, setMintOpen] = useState(false);
  const [mintKind, setMintKind] = useState<ApiKeyKind>("sk");
  const [revealed, setRevealed] = useState<ApiKeyMintResponse | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKeyRow | null>(null);

  const query = useQuery({
    queryKey: ["api-keys", { tenantId }] as const,
    queryFn: () => getApiKeys({ tenant_id: tenantId }),
  });

  const mintMutation = useMutation({
    mutationFn: () => mintApiKey({ tenant_id: tenantId, kind: mintKind }),
    onSuccess: async (resp) => {
      setMintOpen(false);
      setRevealed(resp);
      await qc.invalidateQueries({ queryKey: ["api-keys", { tenantId }] });
    },
    onError: (err) => {
      const body = asApiError(err);
      toast.error("Couldn't mint API key", {
        description: body?.detail ?? body?.error ?? "Check your network and retry.",
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: async () => {
      setConfirmRevoke(null);
      toast.success("API key revoked");
      await qc.invalidateQueries({ queryKey: ["api-keys", { tenantId }] });
    },
    onError: (err) => {
      const body = asApiError(err);
      toast.error("Couldn't revoke", {
        description: body?.detail ?? body?.error ?? "Check your network and retry.",
      });
    },
  });

  const rows = query.data?.results ?? [];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">API keys</CardTitle>
            <CardDescription>
              Server-side and publishable credentials for this tenant's
              integration. The plaintext is shown ONCE at mint time and
              never reconstructable.
            </CardDescription>
          </div>
          <Button onClick={() => setMintOpen(true)}>+ Mint key</Button>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No API keys yet. Mint one to wire this tenant's integration.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prefix</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Env</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead aria-label="Actions" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.prefix}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.kind}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            row.environment === "live"
                              ? "border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300"
                              : "border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300"
                          }
                        >
                          {row.environment}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {row.revoked_at ? (
                          <span className="text-xs text-muted-foreground italic">
                            revoked
                          </span>
                        ) : (
                          <span className="text-xs text-emerald-700 dark:text-emerald-300">
                            active
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.last_used_at
                          ? new Date(row.last_used_at).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(row.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {!row.revoked_at && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmRevoke(row)}
                            data-testid={`apikey-revoke-${row.id}`}
                          >
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mint dialog */}
      <Dialog open={mintOpen} onOpenChange={(open) => open && setMintOpen(true)}>
        <DialogContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={() => setMintOpen(false)}
        >
          <DialogHeader>
            <DialogTitle>Mint a new API key</DialogTitle>
            <DialogDescription>
              The plaintext key is returned exactly once and is never
              reconstructable. Save it before closing the reveal dialog.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {KIND_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/40"
              >
                <input
                  type="radio"
                  name="kind"
                  value={opt.value}
                  checked={mintKind === opt.value}
                  onChange={() => setMintKind(opt.value)}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium">{opt.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {opt.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMintOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => mintMutation.mutate()}
              disabled={mintMutation.isPending}
              data-testid="apikey-mint-confirm"
            >
              {mintMutation.isPending ? "Minting…" : "Mint key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plaintext reveal modal (non-dismissable until acknowledged) */}
      {revealed && (
        <ApiKeyRevealModal
          plaintext={revealed.plaintext}
          prefix={revealed.api_key.prefix}
          onDone={() => setRevealed(null)}
        />
      )}

      {/* Revoke confirm */}
      <Dialog
        open={!!confirmRevoke}
        onOpenChange={(open) => {
          if (!open) setConfirmRevoke(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke this API key?</DialogTitle>
            <DialogDescription>
              Any integration using{" "}
              <code className="font-mono text-xs">{confirmRevoke?.prefix}</code>{" "}
              will start receiving 401 immediately. This cannot be undone — mint
              a new key if you need to restore access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevoke(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmRevoke && revokeMutation.mutate(confirmRevoke.id)}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? "Revoking…" : "Revoke key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ApiKeysTab;
